import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router";
import { makeStyles, shorthands, mergeClasses } from "@fluentui/react-components";
import { formProgressCounts } from "@kmvs/forms-core";
import { apiUrl } from "../utils/api";
import { kfzStepFormSchema } from "../components/risikoKfzStep/schema";
import { BRAND } from "../components/risikoanalyseStep/tokens";
import { sectionColorHex } from "../components/risikoanalyseStep/sectionColor";
import TopBar from "../components/risikoanalyseStep/TopBar";
import Sidebar from "../components/risikoanalyseStep/Sidebar";
import TocMenu from "../components/risikoanalyseStep/TocMenu";
import {
  useTocLayout,
  type TocMode,
} from "../components/risikoanalyseStep/useTocLayout";
import BottomBar from "../components/risikoanalyseStep/BottomBar";
import QuestionBlock from "../components/risikoanalyseStep/QuestionBlock";
import {
  scrollFormToTop,
  scrollScrollableAncestorsToTop,
} from "../components/risikoanalyseStep/scrollToTop";
import { useIsPageChrome } from "../components/windowManager/pageChrome";
import { useTocLeistenOffset } from "../components/risikoanalyseStep/useTocLeistenOffset";
import KfzWelcomeFlow from "../components/risikoKfzStep/KfzWelcomeFlow";
import VehicleClassPicker from "../components/risikoKfzStep/VehicleClassPicker";
import FahrzeugscheinUploader from "../components/risikoKfzStep/FahrzeugscheinUploader";
import VerwendungPicker from "../components/risikoKfzStep/VerwendungPicker";
import LicensePlateInput from "../components/risikoKfzStep/LicensePlateInput";
import StepHero from "../components/risikoKfzStep/StepHero";
import type { Answers } from "../components/risikoanalyseStep/types";

const WELCOME_LS_PREFIX = "risikokfz-welcome-seen:";
const DRAFT_LS_PREFIX = "risikokfz-draft:"; // separate from risikoanalyse-step
const SIDEBAR_WIDTH = 288;
const BOTTOM_BAR_RESERVE = 110;
const AUTOSAVE_DEBOUNCE_MS = 1500;

const useStyles = makeStyles({
  pageState: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: BRAND.ink,
    color: BRAND.white,
    fontFamily: BRAND.fontPrimary,
  },
  pageStateCard: {
    maxWidth: "480px",
    paddingTop: "32px",
    paddingBottom: "32px",
    paddingLeft: "24px",
    paddingRight: "24px",
    backgroundColor: "rgba(255,255,255,0.05)",
    textAlign: "center",
    ...shorthands.borderRadius("8px"),
  },
  pageStateTitle: {
    margin: 0,
    marginBottom: "8px",
    fontFamily: BRAND.fontAccent,
    fontSize: "1.8rem",
    color: BRAND.gold,
  },
  pageStateBody: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.92rem",
    color: "rgba(255,255,255,0.8)",
  },
  shell: {
    minHeight: "100vh",
    display: "flex",
    flexDirection: "column",
    backgroundColor: BRAND.bg,
    color: BRAND.ink,
    fontFamily: BRAND.fontPrimary,
  },
  shellEmbedded: {
    minHeight: "auto",
  },
  body: {
    flexGrow: 1,
    display: "flex",
    overflow: "hidden",
  },
  bodyEmbedded: {
    overflow: "visible",
  },
  /**
   * Ob das Verzeichnis dauerhaft links steht, entscheidet die gemessene
   * CONTAINER-Breite (`useTocLayout`) — nicht mehr eine Viewport-Media-Query.
   * Im schwebenden Fenster war die schlicht falsch: der Viewport ist breit,
   * das Fenster nicht, und deshalb fehlte hier bis #1312/#1313 jede Navigation.
   */
  sidebarDesktop: {
    // Flex-Spalte statt `block`: das `<nav>` der Sidebar wächst darin per
    // `flex-grow` auf die volle Höhe — auch dort, wo `height: 100%` mangels
    // definierter Elternhöhe zu `auto` auflöst.
    display: "flex",
    flexDirection: "column",
    flexShrink: 0,
    width: `${SIDEBAR_WIDTH}px`,
    ...shorthands.borderRight("1px", "solid", "rgba(45,20,43,0.1)"),
  },
  /**
   * Eingebettet scrollt der Fenster-Body, nicht `main` — die Sidebar klebt
   * deshalb am oberen Rand. Ihre Höhe kommt als gemessener Wert dazu
   * (`availableHeight`, inline gesetzt): ohne den stünde das `<aside>` auf
   * Inhaltshöhe und die dunkle Leiste hörte mitten im Formular auf.
   */
  sidebarSticky: {
    position: "sticky",
    top: 0,
    alignSelf: "flex-start",
  },
  main: {
    flexGrow: 1,
    display: "flex",
    flexDirection: "column",
    overflowY: "auto",
  },
  mainEmbedded: {
    overflowY: "visible",
  },
  inner: {
    width: "100%",
    maxWidth: "880px",
    marginLeft: "auto",
    marginRight: "auto",
    paddingTop: "24px",
    paddingLeft: "16px",
    paddingRight: "16px",
    paddingBottom: `${BOTTOM_BAR_RESERVE}px`,
    flexGrow: 1,
    "@media (min-width: 768px)": {
      paddingLeft: "32px",
      paddingRight: "32px",
    },
  },
  innerEmbedded: {
    // BottomBar is sticky and in flow, so no extra reserve needed
    paddingBottom: 0,
  },
  sectionPill: {
    display: "inline-block",
    paddingTop: "4px",
    paddingBottom: "4px",
    paddingLeft: "10px",
    paddingRight: "10px",
    fontSize: "0.72rem",
    fontWeight: 700,
    letterSpacing: "0.05em",
    textTransform: "uppercase",
    color: BRAND.white,
    marginBottom: "8px",
    ...shorthands.borderRadius("999px"),
  },
  titleRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    columnGap: "16px",
    flexWrap: "wrap",
  },
  title: {
    margin: 0,
    marginTop: "4px",
    fontFamily: BRAND.fontAccent,
    fontSize: "clamp(1.6rem, 4vw, 2.4rem)",
    fontWeight: 400,
    color: BRAND.ink,
    lineHeight: 1.15,
    flexGrow: 1,
    minWidth: "200px",
  },
  description: {
    marginTop: "8px",
    marginBottom: 0,
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.95rem",
    color: BRAND.muted,
  },
  questions: {
    marginTop: "16px",
  },
  // Plate label box used inside the kennzeichen sub-step
  plateLabel: {
    display: "block",
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.72rem",
    fontWeight: 600,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: "#5d5664",
    marginBottom: "6px",
  },
  bottomBarFixed: {
    position: "fixed",
    bottom: 0,
    left: 0,
    right: 0,
    zIndex: 15,
  },
  /** Eingerückt, solange das Verzeichnis links steht. */
  bottomBarFixedNebenSidebar: {
    left: `${SIDEBAR_WIDTH}px`,
  },
});

// ── helpers ────────────────────────────────────────────────────────────
/**
 * Fortschritt über die tatsächlich sichtbaren Felder — dieselbe
 * Sichtbarkeitsprüfung, die QuestionBlock beim Rendern anwendet
 * (`collectVisibleFields` aus forms-core). Die frühere Eigenbau-Variante
 * kannte `equalsAny` nicht und hielt diese Form für erfüllt; genau daran
 * hängen im Kfz-Schema sechs Fragen: die vier Fahrerfragen (Fahrername,
 * Fahrerkreis, Fahreralter, Fahrleistung), die Auf-/Einbauten (`bodyType`)
 * und „Sonstiges zur Anschaffung?" (`acquisitionNotes`, nur bei Mietkauf und
 * Leasing). Sie zählten auch dort mit, wo das Formular sie gar nicht zeigt —
 * `acquisitionNotes` sogar bei jedem barbezahlten Fahrzeug jeder Klasse.
 * `isFilled` kannte die Kopie ebenfalls nicht, im Kfz-Schema kommt diese
 * Form aber gar nicht vor.
 *
 * Gezählt wird über `formProgressCounts` und nicht mehr über
 * `collectVisibleFields` direkt: Sonst gäbe es zwei Fortschrittsdefinitionen,
 * die auseinanderlaufen, sobald ein Feld `optional` trägt — die Prozentleiste
 * (`computeFormProgress`) liesse den Slot weg, diese Zeile zählte ihn mit, und
 * der Nutzer läse „100 %" neben „12 von 15".
 */
const computeProgress = (answers: Answers) => formProgressCounts(kfzStepFormSchema, answers);

const HERO_BY_SUBSTEP: Record<string, "fahrzeugart" | "kennzeichen" | "marke_modell" | "aufbauten" | "wert"> = {
  fahrzeugart: "fahrzeugart",
  kennzeichen: "kennzeichen",
  marke_modell: "marke_modell",
  aufbauten: "aufbauten",
  wert: "wert",
};

// ── component ──────────────────────────────────────────────────────────

export type RisikoKfzStepFormProps = {
  /** "public" mode boots from a token, runs the welcome, submits to /public-form/:token.
   *  "embedded" mode is for /risks/new — no welcome, no chrome, parent owns submit. */
  chrome?: "public" | "embedded";
  /**
   * `"auto"` (Default) — die gemessene Container-Breite entscheidet zwischen
   * dauerhaftem Verzeichnis und Menü-Knopf. `"none"` schaltet das Verzeichnis
   * ganz ab (z. B. `RisikoanalyseDetailFormPublic`, wo heute keins existiert
   * und auch keins auftauchen soll).
   */
  toc?: TocMode;
  /** Initial answers (used by embedded mode for prefill). Ignored in public mode (token fetch supplies the prefill). */
  initialAnswers?: Answers;
  /** Embedded-only: called when the user clicks "Risiko erfassen". */
  onSubmitEmbedded?: (answers: Answers) => Promise<void> | void;
  /** Embedded-only: text shown on the final submit button. */
  embeddedSubmitLabel?: string;
  /**
   * Embedded-only: fires on every answers change. Eltern (z.B. NewRiskPage)
   * können daraus z.B. den Window-Titel mit dem aktuellen Kennzeichen
   * aktualisieren. Wird beim Mount **nicht** initial gefeuert (nur bei
   * tatsächlichen Änderungen).
   */
  onAnswersChange?: (answers: Answers) => void;
};

const RisikoKfzStepForm = ({
  chrome = "public",
  toc = "auto",
  initialAnswers,
  onSubmitEmbedded,
  embeddedSubmitLabel = "KFZ speichern",
  onAnswersChange,
}: RisikoKfzStepFormProps) => {
  const styles = useStyles();
  const { token } = useParams();

  const isPublic = chrome === "public";
  const draftKey = (t: string) => DRAFT_LS_PREFIX + t;

  const [loading, setLoading] = useState(isPublic);
  const [valid, setValid] = useState(!isPublic);
  const [submitting, setSubmitting] = useState(false);
  const [submitOk, setSubmitOk] = useState(false);

  const [companyName, setCompanyName] = useState("");
  const [answers, setAnswers] = useState<Answers>(initialAnswers ?? {});

  // Live an Eltern weitermelden — Skip beim ersten Render (Mount), sonst
  // würde der Titel sofort den initialAnswers-Stand übernehmen ohne dass
  // der User etwas getippt hat.
  const skipFirstAnswersChangeRef = useRef(true);
  useEffect(() => {
    if (skipFirstAnswersChangeRef.current) {
      skipFirstAnswersChangeRef.current = false;
      return;
    }
    onAnswersChange?.(answers);
  }, [answers, onAnswersChange]);

  const [showWelcome, setShowWelcome] = useState<boolean>(() => {
    if (!isPublic || !token) return false;
    try {
      return window.localStorage.getItem(WELCOME_LS_PREFIX + token) !== "1";
    } catch {
      return true;
    }
  });

  const [sectionIdx, setSectionIdx] = useState(0);
  const [subStepIdx, setSubStepIdx] = useState(0);
  const [savingDraft, setSavingDraft] = useState(false);
  const [draftSavedAt, setDraftSavedAt] = useState<string | null>(null);
  const [fahrzeugscheinFile, setFahrzeugscheinFile] = useState<File | null>(null);
  const [ocrFilledKeys, setOcrFilledKeys] = useState<Set<string>>(new Set());
  const [ocrError, setOcrError] = useState<string | null>(null);
  const mainRef = useRef<HTMLDivElement | null>(null);
  const shellRef = useRef<HTMLDivElement | null>(null);
  /**
   * Dauerhaftes Verzeichnis oder Menü-Knopf? Das entscheidet die gemessene
   * Breite des Shell-Elements — nicht `chrome` und nicht der Viewport.
   * Vollseite/maximiertes Fenster liegen über der Schwelle, ein
   * wiederhergestelltes Fenster (1140 px) darunter.
   */
  const { layout: tocLayout, availableHeight } = useTocLayout(shellRef, toc);
  /**
   * Vollseite (Window-State `"page"`)? Dort löst sich die Schritt-Leiste aus
   * der Inhaltsspalte und läuft über die volle Breite (#1307) — sie liegt dann
   * auch über dem unteren Rand des Verzeichnisses.
   */
  const istVollseite = useIsPageChrome();
  // Wo die Schritt-Leiste der Vollseite beginnt: GEMESSEN am rechten Rand des
  // Verzeichnisses, nicht die feste Sidebar-Breite — das Verzeichnis ist in der
  // Vollseite eingerückt, sonst lag die Leiste über seinem unteren Ende.
  const { ref: verzeichnisRef, offset: tocLeistenOffset } = useTocLeistenOffset();
  const autosaveTimerRef = useRef<number | null>(null);
  const draftLoadedRef = useRef(!isPublic);

  // Public: load token + stammdaten + restore draft
  useEffect(() => {
    if (!isPublic) return;
    const run = async () => {
      if (!token) {
        setValid(false);
        setLoading(false);
        return;
      }
      try {
        const res = await fetch(apiUrl(`public-form/${token}`));
        if (!res.ok) {
          setValid(false);
          return;
        }
        setValid(true);
        try {
          const data = await res.json();
          const c = data?.contact;
          if (c) setCompanyName(c.displayName || c.lastname || "");
        } catch {}

        // Use the dedicated KFZ draft prefix.
        const draftRaw = window.localStorage.getItem(draftKey(token));
        if (draftRaw) {
          try {
            const parsed = JSON.parse(draftRaw);
            if (parsed && typeof parsed === "object") {
              setAnswers((parsed.answers as Answers) ?? {});
              if (typeof parsed.sectionIdx === "number") setSectionIdx(parsed.sectionIdx);
              if (typeof parsed.subStepIdx === "number") setSubStepIdx(parsed.subStepIdx);
              if (typeof parsed.savedAt === "string") setDraftSavedAt(parsed.savedAt);
            }
          } catch {}
        }
        draftLoadedRef.current = true;
      } catch {
        setValid(false);
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [token, isPublic]);

  // Debounced auto-save (public only — embedded saves are owned by parent)
  useEffect(() => {
    if (!isPublic || !draftLoadedRef.current || !token) return;
    if (autosaveTimerRef.current) window.clearTimeout(autosaveTimerRef.current);
    autosaveTimerRef.current = window.setTimeout(() => {
      const payload = {
        answers,
        sectionIdx,
        subStepIdx,
        savedAt: new Date().toISOString(),
      };
      try {
        window.localStorage.setItem(draftKey(token), JSON.stringify(payload));
        setDraftSavedAt(payload.savedAt);
      } catch {}
    }, AUTOSAVE_DEBOUNCE_MS);
    return () => {
      if (autosaveTimerRef.current) window.clearTimeout(autosaveTimerRef.current);
    };
  }, [answers, sectionIdx, subStepIdx, token, isPublic]);

  const handleAnswerChange = (key: string, value: unknown) => {
    setAnswers((prev) => ({ ...prev, [key]: value }));
    // The user edited a field that was OCR-filled — drop its chip so the
    // green "aus Fahrzeugschein" tag disappears for that field.
    setOcrFilledKeys((prev) => {
      if (!prev.has(key)) return prev;
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
  };

  const handleFahrzeugscheinExtracted = (
    data: Record<string, string>,
    fileUrl: string,
  ) => {
    setOcrError(null);
    setAnswers((prev) => ({ ...prev, ...data, fahrzeugscheinUrl: fileUrl }));
    setOcrFilledKeys(new Set(Object.keys(data)));
  };

  const totalSubSteps = useMemo(
    () => kfzStepFormSchema.sections.reduce((s, sec) => s + sec.subSteps.length, 0),
    []
  );
  const flatIndex = useMemo(() => {
    let i = 0;
    for (let s = 0; s < sectionIdx; s += 1) i += kfzStepFormSchema.sections[s].subSteps.length;
    return i + subStepIdx;
  }, [sectionIdx, subStepIdx]);
  const isLastStep = flatIndex === totalSubSteps - 1;
  const canPrev = flatIndex > 0;

  const progress = useMemo(() => computeProgress(answers), [answers]);
  const overallFraction = progress.total === 0 ? 0 : progress.answered / progress.total;
  const stepFraction = (flatIndex + 1) / totalSubSteps;

  /**
   * Nach JEDEM Schrittwechsel steht der Inhalt oben — egal ob Weiter, Zurück
   * oder Klick im Verzeichnis.
   *
   * Bewusst als Effekt und nicht in den Klick-Handlern (so wie in
   * `RisikoImmobilieStepForm`): `setSectionIdx`/`setSubStepIdx` rendern erst
   * danach, ein Scroll im Handler betrifft also noch den ALTEN Inhalt und wird
   * vom folgenden Render abgeschnitten. Der Effekt läuft nach dem Commit des
   * neuen Schritts.
   *
   * Drei Container, weil das Formular in drei Rahmen läuft: `main` (öffentliche
   * Vollseite), Fenster/`window` (`scrollFormToTop`) und — im schwebenden
   * Fenster — ein `overflow: auto`-`<div>` zwischen beiden. Der dritte kam mit
   * dem Verzeichnis im Fenster dazu: ohne ihn wechselt der Schritt, die Ansicht
   * bleibt aber auf halber Höhe stehen.
   *
   * Ohne Animation (`"auto"`): der Inhalt ist beim Schrittwechsel vollständig
   * ausgetauscht, es gibt also nichts, woran ein Gleiten vorbeiführen würde —
   * und eine laufende Smooth-Animation bricht jeder konkurrierende Scroll still
   * ab, der Sprung bliebe mal aus und mal nicht.
   */
  const ersterSchrittRender = useRef(true);
  useEffect(() => {
    // Beim ersten Rendern gibt es nichts zurückzusetzen.
    if (ersterSchrittRender.current) {
      ersterSchrittRender.current = false;
      return;
    }
    const el = mainRef.current;
    // `Element.scrollTo` fehlt in jsdom — ein Sprung darf nie werfen.
    if (el && typeof el.scrollTo === "function") {
      el.scrollTo({ top: 0, behavior: "auto" });
    }
    scrollFormToTop("auto");
    scrollScrollableAncestorsToTop(shellRef.current, "auto");
  }, [sectionIdx, subStepIdx]);

  const goPrev = () => {
    if (subStepIdx > 0) setSubStepIdx((s) => s - 1);
    else if (sectionIdx > 0) {
      const prev = sectionIdx - 1;
      setSectionIdx(prev);
      setSubStepIdx(kfzStepFormSchema.sections[prev].subSteps.length - 1);
    }
  };

  const goNext = () => {
    const sec = kfzStepFormSchema.sections[sectionIdx];
    if (subStepIdx + 1 < sec.subSteps.length) setSubStepIdx((s) => s + 1);
    else if (sectionIdx + 1 < kfzStepFormSchema.sections.length) {
      setSectionIdx((s) => s + 1);
      setSubStepIdx(0);
    }
  };

  const handleSelect = (s: number, ss: number) => {
    setSectionIdx(s);
    setSubStepIdx(ss);
  };

  const handleWelcomeComplete = () => {
    setShowWelcome(false);
    if (token) {
      try {
        window.localStorage.setItem(WELCOME_LS_PREFIX + token, "1");
      } catch {}
    }
  };

  const handleSaveDraft = async () => {
    if (!token) return;
    setSavingDraft(true);
    try {
      const payload = {
        answers,
        sectionIdx,
        subStepIdx,
        savedAt: new Date().toISOString(),
      };
      try {
        window.localStorage.setItem(draftKey(token), JSON.stringify(payload));
        setDraftSavedAt(payload.savedAt);
      } catch {}
    } finally {
      window.setTimeout(() => setSavingDraft(false), 250);
    }
  };

  const handleSubmit = async () => {
    if (chrome === "embedded") {
      if (!onSubmitEmbedded) return;
      setSubmitting(true);
      try {
        await onSubmitEmbedded(answers);
      } finally {
        setSubmitting(false);
      }
      return;
    }
    if (!token) return;
    setSubmitting(true);
    try {
      const res = await fetch(apiUrl(`public-form/${token}`), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ answers, version: "kfz-step-v1" }),
      });
      if (res.ok) {
        try {
          window.localStorage.removeItem(draftKey(token));
        } catch {}
        setSubmitOk(true);
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Loading / invalid / submitOk (public only)
  if (isPublic && loading) {
    return (
      <div className={styles.pageState}>
        <div className={styles.pageStateCard}>
          <div className={styles.pageStateBody}>Formular wird geladen…</div>
        </div>
      </div>
    );
  }
  if (isPublic && !valid) {
    return (
      <div className={styles.pageState}>
        <div className={styles.pageStateCard}>
          <div className={styles.pageStateBody}>Der Link ist ungültig oder abgelaufen.</div>
        </div>
      </div>
    );
  }
  if (isPublic && submitOk) {
    return (
      <div className={styles.pageState}>
        <div className={styles.pageStateCard}>
          <h2 className={styles.pageStateTitle}>Vielen Dank</h2>
          <p className={styles.pageStateBody}>
            Ihr Fahrzeug wurde übermittelt. Wir melden uns mit den nächsten Schritten.
          </p>
        </div>
      </div>
    );
  }

  if (isPublic && showWelcome) {
    return <KfzWelcomeFlow companyName={companyName} onComplete={handleWelcomeComplete} />;
  }

  const section = kfzStepFormSchema.sections[sectionIdx];
  const subStep = section.subSteps[subStepIdx];
  const accent = sectionColorHex[section.color];
  const heroKind = HERO_BY_SUBSTEP[subStep.key];

  // Custom render for sub-steps with bespoke widgets
  const renderQuestions = () => {
    // Fahrzeugart → tile picker
    if (subStep.key === "fahrzeugart") {
      return (
        <div>
          <VehicleClassPicker
            value={typeof answers.vehicleClass === "string" ? answers.vehicleClass : ""}
            onChange={(v) => handleAnswerChange("vehicleClass", v)}
          />
        </div>
      );
    }

    // Verwendung → klassenabhängiger tile picker
    if (subStep.key === "verwendung") {
      return (
        <div>
          <VerwendungPicker
            vehicleClass={typeof answers.vehicleClass === "string" ? answers.vehicleClass : ""}
            value={typeof answers.vehicleUsage === "string" ? answers.vehicleUsage : ""}
            onChange={(v) => handleAnswerChange("vehicleUsage", v)}
          />
        </div>
      );
    }

    // Kennzeichen → first question is the visual plate input, the rest fall back to QuestionBlock
    if (subStep.key === "kennzeichen") {
      const [first, ...rest] = subStep.questions;
      return (
        <div>
          <section style={{ paddingBottom: 14, borderBottom: "1px dashed " + BRAND.borderDashed }}>
            <h3
              style={{
                fontFamily: BRAND.fontAccent,
                fontSize: "1.2rem",
                color: BRAND.ink,
                margin: 0,
                marginBottom: 6,
              }}
            >
              {first.question}
            </h3>
            <span className={styles.plateLabel}>Kennzeichen</span>
            <LicensePlateInput
              value={typeof answers.licensePlate === "string" ? answers.licensePlate : ""}
              onChange={(v) => handleAnswerChange("licensePlate", v)}
            />
          </section>
          {rest.map((q, i) => (
            <QuestionBlock
              key={`${subStep.key}-${i + 1}`}
              question={q}
              answers={answers}
              onChange={handleAnswerChange}
              ocrFilledKeys={ocrFilledKeys}
            />
          ))}
        </div>
      );
    }

    // default — full QuestionBlock list
    return subStep.questions.map((q, idx) => (
      <QuestionBlock
        key={`${subStep.key}-${idx}`}
        question={q}
        answers={answers}
        onChange={handleAnswerChange}
        ocrFilledKeys={ocrFilledKeys}
      />
    ));
  };

  const isEmbedded = chrome === "embedded";

  // Ein gemeinsamer Satz Verzeichnis-Props für beide Darstellungen — die
  // Sidebar links und das Panel hinter dem Knopf zeigen dasselbe.
  const tocProps = {
    sections: kfzStepFormSchema.sections,
    currentSectionIdx: sectionIdx,
    currentSubStepIdx: subStepIdx,
    onSelect: handleSelect,
    progressFraction: overallFraction,
    progressDetail: `${progress.answered}/${progress.total} Felder`,
    // Im Fenster ist die Kopfzeile des Formulars nicht sichtbar — der
    // Verzeichniskopf ist dort das einzige, was die Risikoart benennt.
    eyebrow: "KFZ",
    heading: "Inhaltsverzeichnis",
    ariaLabel: "Inhaltsverzeichnis der KFZ-Erfassung",
    // `farbschema` bleibt bewusst auf dem Default „abschnitte" — anders als bei
    // der Immobilie (#1312), die auf „neutral" umgestellt wurde. Grund ist die
    // Einheitlichkeit mit der öffentlichen Kundenstrecke: dort steht über der
    // Schritt-Überschrift die Abschnitts-Pille in der Abschnittsfarbe
    // (`sectionPill`, `backgroundColor: accent`, unten hinter `!isEmbedded`),
    // ein neutrales Verzeichnis würde diesen Bezug zerreißen. Eingebettet ist
    // die Pille zwar ausgeblendet, aber beide Wege sollen dieselbe Optik haben.
  };

  return (
    <div
      ref={shellRef}
      className={mergeClasses(styles.shell, isEmbedded && styles.shellEmbedded)}
      // Breite des Inhaltsverzeichnisses für die Schritt-Leiste: sie beginnt in
      // der Vollseite an dessen rechtem Rand, damit das Verzeichnis bis zum
      // unteren Rand durchläuft. Ohne Verzeichnis bleibt die Variable ungesetzt
      // und die Leiste nimmt die volle Breite.
      style={
        tocLayout === "sidebar"
          ? ({ "--toc-leiste-breite": `${tocLeistenOffset ?? SIDEBAR_WIDTH}px` } as React.CSSProperties)
          : undefined
      }
    >
      {!isEmbedded && (
        // Genau ein Auslöser je Kontext: das Verzeichnis hängt jetzt am
        // `TocMenu` im Formular, nicht mehr am Hamburger der TopBar.
        <TopBar customerName={companyName} menuMode="never" />
      )}
      <div className={mergeClasses(styles.body, isEmbedded && styles.bodyEmbedded)}>
        {tocLayout === "sidebar" && (
          <aside
            ref={verzeichnisRef}
            className={mergeClasses(styles.sidebarDesktop, isEmbedded && styles.sidebarSticky)}
            /*
              Eingebettet klebt das `<aside>` oben und hat deshalb keine Höhe
              aus dem Layout — ohne den gemessenen Wert endete die dunkle
              Leiste auf Inhaltshöhe. In der Vollseite streckt der
              Flex-Container das `<aside>` bereits korrekt.
            */
            style={isEmbedded && availableHeight != null ? { height: availableHeight } : undefined}
          >
            <Sidebar
              {...tocProps}
              maxHeight={isEmbedded ? availableHeight : undefined}
              /*
                Vollseite: die Schritt-Leiste läuft dort über die volle Breite
                und liegt über dem Fuß des Verzeichnisses (#1307) — so viel
                Freiraum unter dem letzten Eintrag hält ihn erreichbar.
              */
              bottomInset={isEmbedded && istVollseite ? BOTTOM_BAR_RESERVE : 0}
            />
          </aside>
        )}

        <main className={mergeClasses(styles.main, isEmbedded && styles.mainEmbedded)} ref={mainRef}>
          <div className={mergeClasses(styles.inner, isEmbedded && styles.innerEmbedded)}>
            {tocLayout === "menu" && (
              <TocMenu
                {...tocProps}
                maxHeight={availableHeight}
                kontextText={`Schritt ${flatIndex + 1} von ${totalSubSteps}`}
              />
            )}
            {!isEmbedded && (
              <div className={styles.sectionPill} style={{ backgroundColor: accent }}>
                {section.title}
              </div>
            )}
            {subStep.key === "fahrzeugart" && (
              <>
                <FahrzeugscheinUploader
                  value={fahrzeugscheinFile}
                  onChange={(f) => {
                    setFahrzeugscheinFile(f);
                    if (!f) {
                      setOcrFilledKeys(new Set());
                      setOcrError(null);
                    }
                  }}
                  onExtracted={handleFahrzeugscheinExtracted}
                  onError={(msg) => setOcrError(msg)}
                />
                {ocrError && (
                  <div
                    role="alert"
                    style={{
                      marginTop: -10,
                      marginBottom: 16,
                      padding: "10px 14px",
                      borderRadius: 6,
                      backgroundColor: "rgba(155,28,28,0.08)",
                      color: "#9b1c1c",
                      fontSize: "0.85rem",
                    }}
                  >
                    Auslesen des Fahrzeugscheins fehlgeschlagen — bitte Felder manuell ausfüllen.
                  </div>
                )}
              </>
            )}
            <div className={styles.titleRow}>
              <h2 className={styles.title}>{subStep.title}</h2>
              {heroKind && <StepHero kind={heroKind} />}
            </div>
            {subStep.description && (
              <p className={styles.description}>{subStep.description}</p>
            )}
            <div className={styles.questions}>{renderQuestions()}</div>
            {isEmbedded && (
              <BottomBar
                sticky
                onPrev={goPrev}
                onNext={goNext}
                onSubmit={handleSubmit}
                onSaveDraft={() => {/* embedded: no draft */}}
                canPrev={canPrev}
                isLastStep={isLastStep}
                submitting={submitting}
                savingDraft={false}
                draftSavedAt={null}
                progressLabel={`Schritt ${flatIndex + 1} von ${totalSubSteps}`}
                progressFraction={stepFraction}
                submitLabel={embeddedSubmitLabel}
                showSaveDraft={false}
              />
            )}
          </div>
        </main>
      </div>

      {!isEmbedded && (
        <div
          className={mergeClasses(
            styles.bottomBarFixed,
            tocLayout === "sidebar" && styles.bottomBarFixedNebenSidebar,
          )}
        >
          <BottomBar
            onPrev={goPrev}
            onNext={goNext}
            onSubmit={handleSubmit}
            onSaveDraft={handleSaveDraft}
            canPrev={canPrev}
            isLastStep={isLastStep}
            submitting={submitting}
            savingDraft={savingDraft}
            draftSavedAt={draftSavedAt}
            progressLabel={`Schritt ${flatIndex + 1} von ${totalSubSteps} · ${section.title}`}
            progressFraction={stepFraction}
          />
        </div>
      )}
    </div>
  );
};

// Wrapper used by the public route to pull `token` from useParams.
const RisikoKfzStepFormPublic = () => <RisikoKfzStepForm chrome="public" />;

export { RisikoKfzStepForm };
export default RisikoKfzStepFormPublic;
